<template>
  <div
    class="progress-dots-container"
    :class="[`progress-dots-container--${animation}`]"
  >
    <div class="progress-dots" :class="[`progress-dots--${animation}`]" />
  </div>
</template>

<script>
  export default {
    name: 'ProgressDots',
    props: {
      animation: {
        type: String,
        default: 'pulse',
        validator: (value) => {
          const ANIMATIONS = [
            'pulse',
            'elastic',
            'flashing',
            'collision',
            'falling',
            'typing',
          ];
          return ANIMATIONS.includes(value);
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  $color-one: #eb5757;
  $color-two: #006742;
  $color-three: #17355c;

  .progress-dots-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    padding: 2px 0;
    margin: 0 -2%;
    overflow: hidden;

    min-width: 50px;

    &--typing {
      padding: 6px 0;
    }
    &--falling {
      padding: 8px 0;
    }
  }
  .progress-dots {
    position: relative;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: $color-two;
    color: $color-two;

    &::before,
    &::after {
      content: '';
      display: inline-block;
      position: absolute;
      top: 0;
      width: 10px;
      height: 10px;
    }

    &::before {
      background-color: $color-one;
      color: $color-one;
    }
    &::after {
      background-color: $color-three;
      color: $color-three;
    }

    &--pulse {
      position: relative;
      left: -9999px;
      border-radius: 5px;
      box-shadow: 9999px 0 0 -5px;
      animation: dot-pulse 1800ms infinite linear;
      animation-delay: 250ms;

      &::before,
      &::after {
        border-radius: 5px;
      }
      &::before {
        box-shadow: 9980px 0 0 -5px;
        animation: dot-pulse-before 1800ms infinite linear;
        animation-delay: 0ms;
      }
      &::after {
        box-shadow: 10010px 0 0 -5px;
        animation: dot-pulse-after 1800ms infinite linear;
        animation-delay: 500ms;
      }

      @keyframes dot-pulse-before {
        0% {
          box-shadow: 9980px 0 0 -5px;
        }
        30% {
          box-shadow: 9980px 0 0 2px;
        }
        60%,
        100% {
          box-shadow: 9980px 0 0 -5px;
        }
      }
      @keyframes dot-pulse {
        0% {
          box-shadow: 9999px 0 0 -5px;
        }
        30% {
          box-shadow: 9999px 0 0 2px;
        }
        60%,
        100% {
          box-shadow: 9999px 0 0 -5px;
        }
      }
      @keyframes dot-pulse-after {
        0% {
          box-shadow: 10010px 0 0 -5px;
        }
        30% {
          box-shadow: 10010px 0 0 2px;
        }
        60%,
        100% {
          box-shadow: 10010px 0 0 -5px;
        }
      }
    }

    &--elastic {
      animation: dot-elastic 1000ms infinite linear;

      &::before {
        left: -15px;
        border-radius: 5px;
        animation: dot-elastic-before 1000ms infinite linear;
      }
      &::after {
        left: 15px;
        border-radius: 5px;
        animation: dot-elastic-after 1000ms infinite linear;
      }

      @keyframes dot-elastic-before {
        0% {
          transform: scale(1, 1);
        }
        25% {
          transform: scale(1, 1.5);
        }
        50% {
          transform: scale(1, 0.67);
        }
        75% {
          transform: scale(1, 1);
        }
        100% {
          transform: scale(1, 1);
        }
      }
      @keyframes dot-elastic {
        0% {
          transform: scale(1, 1);
        }
        25% {
          transform: scale(1, 1);
        }
        50% {
          transform: scale(1, 1.5);
        }
        75% {
          transform: scale(1, 1);
        }
        100% {
          transform: scale(1, 1);
        }
      }
      @keyframes dot-elastic-after {
        0% {
          transform: scale(1, 1);
        }
        25% {
          transform: scale(1, 1);
        }
        50% {
          transform: scale(1, 0.67);
        }
        75% {
          transform: scale(1, 1.5);
        }
        100% {
          transform: scale(1, 1);
        }
      }
    }

    &--flashing {
      position: relative;
      border-radius: 5px;
      animation: dot-flashing-two 1000ms infinite linear alternate;
      animation-delay: 500ms;

      &::before {
        left: -15px;
        border-radius: 5px;
        animation: dot-flashing-one 1000ms infinite alternate;
        animation-delay: 0ms;
      }
      &::after {
        left: 15px;
        border-radius: 5px;
        animation: dot-flashing-three 1000ms infinite alternate;
        animation-delay: 1000ms;
      }

      @keyframes dot-flashing-one {
        0% {
          background-color: $color-one;
        }
        50%,
        100% {
          background-color: rgba($color-one, 0.2);
        }
      }
      @keyframes dot-flashing-two {
        0% {
          background-color: $color-two;
        }
        50%,
        100% {
          background-color: rgba($color-two, 0.2);
        }
      }
      @keyframes dot-flashing-three {
        0% {
          background-color: $color-three;
        }
        50%,
        100% {
          background-color: rgba($color-three, 0.2);
        }
      }
    }

    &--collision {
      border-radius: 5px;

      &::before {
        left: -10px;
        width: 10px;
        height: 10px;
        border-radius: 5px;
        animation: dot-collision-before 2000ms infinite ease-in;
      }
      &::after {
        left: 10px;
        width: 10px;
        height: 10px;
        border-radius: 5px;
        animation: dot-collision-after 2000ms infinite ease-in;
        animation-delay: 1000ms;
      }

      @keyframes dot-collision-before {
        0%,
        50%,
        75%,
        100% {
          transform: translateX(0);
        }
        25% {
          transform: translateX(-15px);
        }
      }
      @keyframes dot-collision-after {
        0%,
        50%,
        75%,
        100% {
          transform: translateX(0);
        }
        25% {
          transform: translateX(15px);
        }
      }
    }

    &--falling {
      position: relative;
      left: -9999px;
      border-radius: 5px;
      box-shadow: 9999px 0 0 0 $color-one;
      animation: dot-falling 1600ms infinite linear;
      animation-delay: 400ms;

      &::before {
        border-radius: 5px;
        animation: dot-falling-before 1600ms infinite linear;
        animation-delay: 0ms;
      }
      &::after {
        border-radius: 5px;
        animation: dot-falling-after 1600ms infinite linear;
        animation-delay: 600ms;
      }

      @keyframes dot-falling {
        0% {
          box-shadow: 9999px -10px 0 0 rgba(152, 128, 255, 0);
        }
        25%,
        50%,
        75% {
          box-shadow: 9999px 0 0 0 $color-two;
        }
        100% {
          box-shadow: 9999px 10px 0 0 rgba(152, 128, 255, 0);
        }
      }
      @keyframes dot-falling-before {
        0% {
          box-shadow: 9984px -10px 0 0 rgba(152, 128, 255, 0);
        }
        25%,
        50%,
        75% {
          box-shadow: 9984px 0 0 0 $color-one;
        }
        100% {
          box-shadow: 9984px 10px 0 0 rgba(152, 128, 255, 0);
        }
      }
      @keyframes dot-falling-after {
        0% {
          box-shadow: 10014px -10px 0 0 rgba(152, 128, 255, 0);
        }
        25%,
        50%,
        75% {
          box-shadow: 10014px 0 0 0 $color-three;
        }
        100% {
          box-shadow: 10014px 10px 0 0 rgba(152, 128, 255, 0);
        }
      }
    }

    &--typing {
      position: relative;
      left: -9999px;
      border-radius: 5px;
      box-shadow:
        9984px 0 0 0 $color-one,
        9999px 0 0 0 $color-two,
        10014px 0 0 0 $color-three;
      animation: dot-typing 1500ms infinite linear;

      @keyframes dot-typing {
        0% {
          box-shadow:
            9984px 0 0 0 $color-one,
            9999px 0 0 0 $color-two,
            10014px 0 0 0 $color-three;
        }
        16.667% {
          box-shadow:
            9984px -6px 0 0 $color-one,
            9999px 0 0 0 $color-two,
            10014px 0 0 0 $color-three;
        }
        33.333% {
          box-shadow:
            9984px 0 0 0 $color-one,
            9999px 0 0 0 $color-two,
            10014px 0 0 0 $color-three;
        }
        50% {
          box-shadow:
            9984px 0 0 0 $color-one,
            9999px -6px 0 0 $color-two,
            10014px 0 0 0 $color-three;
        }
        66.667% {
          box-shadow:
            9984px 0 0 0 $color-one,
            9999px 0 0 0 $color-two,
            10014px 0 0 0 $color-three;
        }
        83.333% {
          box-shadow:
            9984px 0 0 0 $color-one,
            9999px 0 0 0 $color-two,
            10014px -6px 0 0 $color-three;
        }
        100% {
          box-shadow:
            9984px 0 0 0 $color-one,
            9999px 0 0 0 $color-two,
            10014px 0 0 0 $color-three;
        }
      }
    }
  }
</style>
